import { DeclarationV3Status as Status } from '@/@types/Declarations.type';
import type {
	EngineAnnualSummary,
	EngineCycleState,
	EngineOverview,
	EngineResult,
	EngineYearMonth,
} from '@/shared/services/declarationsNew.service';
import { periodOf, splitPeriod } from './periods';
import { DECLARABLE_BY_CODE, type DeclarableRegime } from './regimes';

/**
 * Projects the declarations/new overview (`GET /declarations/new/me/overview`)
 * onto the month/annual row shape that Redux (`taxProfile.declarationsOverview`
 * and `taxProfile.annualDeclarations`) has always carried, so the screens that
 * still read that state (income widgets, regularization, checkout catalog,
 * onboarding selectors, package progress) keep working without knowing which
 * engine produced it.
 *
 * It replaces the former declarations v3 overview adapter. The engine is the
 * only source of truth; this file only translates its vocabulary:
 *
 *   engine cell state                      row status            isAcquired
 *   -------------------------------------  --------------------  ----------
 *   presentada / por_pagar / pagada        PRESENTED             false
 *   sin_presentar                          PENDING               false
 *   sin_presentar, receiptChecked false    NOT_STARTED_ACTIVITY  false
 *   listo_para_aprobar, locked (no total)  PENDING, or FUTURE     false
 *                                          while its period is open
 *   listo_para_aprobar, with total         READY_FOR_APPROVAL    true
 *   preparando / necesita_algo /
 *   presentando                            IN_PROGRESS           true
 *   en_revision                            IN_REVIEW             true
 *   mes_en_curso                           FUTURE                false
 *
 * `preparando` is also how the engine reports the months a Regularizacion
 * total purchase covers, so those months read as covered (IN_PROGRESS), and
 * the annuals it covers arrive as `purchased` and read as IN_PROGRESS too.
 *
 * Months and regimes the engine has no cell for keep the old grid contract:
 * every month carries one row per declarable regime, FUTURE when the period is
 * still open and NOT_STARTED_ACTIVITY when it is over. `useMonthlyDeclarations`
 * and the checkout catalog rely on that to tell "open period" from "no data".
 *
 * Not carried over, because the overview does not expose it: acquisition
 * provenance (`acquisitionAdditionalInformation`, which only the checkout
 * approval-flow banner read), per-declaration due dates and days late, and the
 * annual precalculation, deductions and refund detail.
 */

const ROLLUP_ORDER: Status[] = [
	Status.READY_FOR_APPROVAL,
	Status.IN_PROGRESS,
	Status.PRESENTED,
	Status.ACQUIRED,
	Status.PENDING,
];

const INACTIVE_STATUSES: Status[] = [Status.FUTURE, Status.NOT_STARTED_ACTIVITY];

const REGIMES_IN_ORDER: DeclarableRegime[] = ['resico', 'platec', 'business'];

const PREVIOUS_MONTH_STAYS_OPEN_UNTIL_DAY = 14;

const ANNUAL_RESULT = { a_pagar: 'TO_PAY', saldo_a_favor: 'IN_FAVOR', en_ceros: 'ZERO' } as const satisfies Record<EngineResult, string>;

export type LegacyDeclaration = {
	type: DeclarableRegime;
	month: number;
	year: number;
	status: Status;
	income: number;
	isAcquired: boolean;
	isFuture: boolean;
	/** Only on PRESENTED rows: false while the engine still waits for the payment. */
	paid?: boolean;
};

export type LegacyMonth = {
	month: number;
	year: number;
	/** `total` plus one entry per regime with income (`resico`, `platec`, `business`, `salarios`). */
	income: Record<string, number> & { total: number };
	regimes: string[];
	status: Status;
	paid: boolean;
	declarations: LegacyDeclaration[];
};

export type LegacyAnnual = {
	year: number;
	type: 'annual';
	income: number;
	status: Status;
	isAcquired: boolean;
	hasPresented: boolean;
	dueDate: string;
	daysLate?: number;
	paid?: boolean;
	result?: 'TO_PAY' | 'IN_FAVOR' | 'ZERO';
	/** Negative when the result is in favor. */
	resultAmount?: number;
};

export type LegacyYear = {
	year: number;
	monthly: LegacyMonth[];
	annual?: LegacyAnnual;
};

export type LegacyOverview = { data: LegacyYear[] };

type Clock = { current: string; previous: string; day: number };

const clockOf = (today: string): Clock => {
	const [year, month, day] = today.split('-').map(Number);
	return {
		current: periodOf(year, month),
		previous: month === 1 ? periodOf(year - 1, 12) : periodOf(year, month - 1),
		day,
	};
};

const isOpenPeriod = (period: string, clock: Clock) =>
	period >= clock.current || (period === clock.previous && clock.day < PREVIOUS_MONTH_STAYS_OPEN_UNTIL_DAY);

/** `today` is the engine's `overview.today` (`YYYY-MM-DD`); a `Date` falls back to the browser clock. */
export const isPeriodOpen = (period: string, today: string | Date) =>
	isOpenPeriod(
		period,
		clockOf(typeof today === 'string' ? today : `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`),
	);

type Cell = { regime: DeclarableRegime; state: EngineCycleState; total: number | null; income?: number; receiptChecked?: boolean };

const cellsOf = (month: EngineYearMonth): Cell[] => {
	const raw = month.cells?.length
		? month.cells
		: month.state
			? [
					{
						regime: month.regime ?? month.regimes[0],
						state: month.state,
						total: month.total,
						income: month.income,
						receiptChecked: month.receiptChecked,
					},
				]
			: [];
	return raw.flatMap((cell) => {
		const regime = cell.regime ? DECLARABLE_BY_CODE[cell.regime] : undefined;
		return regime ? [{ regime, state: cell.state, total: cell.total, income: cell.income, receiptChecked: cell.receiptChecked }] : [];
	});
};

type Projection = { status: Status; isAcquired: boolean };

const projectCell = ({ state, total, receiptChecked }: Cell, period: string, clock: Clock): Projection => {
	switch (state) {
		case 'presentada':
		case 'por_pagar':
		case 'pagada':
			return { status: Status.PRESENTED, isAcquired: false };
		case 'sin_presentar':
			return { status: receiptChecked === false ? Status.NOT_STARTED_ACTIVITY : Status.PENDING, isAcquired: false };
		case 'listo_para_aprobar':
			if (total !== null) return { status: Status.READY_FOR_APPROVAL, isAcquired: true };
			return { status: period >= clock.previous && isOpenPeriod(period, clock) ? Status.FUTURE : Status.PENDING, isAcquired: false };
		case 'preparando':
		case 'necesita_algo':
		case 'presentando':
			return { status: Status.IN_PROGRESS, isAcquired: true };
		case 'en_revision':
			return { status: Status.IN_REVIEW, isAcquired: true };
		case 'mes_en_curso':
			return { status: Status.FUTURE, isAcquired: false };
	}
};

const rollupRank = (status: Status) => {
	const index = ROLLUP_ORDER.indexOf(status);
	return index === -1 ? ROLLUP_ORDER.length : index;
};

const monthOf = (month: EngineYearMonth, clock: Clock): LegacyMonth => {
	const { year, month: monthNumber } = splitPeriod(month.period);
	const open = isOpenPeriod(month.period, clock);
	const cells = cellsOf(month);
	const cellByRegime = new Map(cells.map((cell) => [cell.regime, cell]));

	const all: LegacyDeclaration[] = REGIMES_IN_ORDER.map((type) => {
		const base = { type, month: monthNumber, year, isFuture: open };
		const cell = cellByRegime.get(type);
		if (!cell) {
			return { ...base, status: open ? Status.FUTURE : Status.NOT_STARTED_ACTIVITY, income: 0, isAcquired: false };
		}
		const { status, isAcquired } = projectCell(cell, month.period, clock);
		return {
			...base,
			status,
			isAcquired,
			income: cell.income ?? (cells.length === 1 ? (month.income ?? 0) : 0),
			...(status === Status.PRESENTED ? { paid: cell.state !== 'por_pagar' } : {}),
		};
	});

	const active = all
		.filter((declaration) => !INACTIVE_STATUSES.includes(declaration.status))
		.sort((a, b) => rollupRank(a.status) - rollupRank(b.status));
	const declarations = active.length > 0 ? active : all;

	const income: Record<string, number> = {};
	for (const declaration of all) {
		if (declaration.income > 0) income[declaration.type] = declaration.income;
	}
	if (month.payroll) income.salarios = month.income ?? month.payroll.gross;
	const total = month.income ?? Object.values(income).reduce((sum, value) => sum + value, 0);

	const status =
		month.payroll && declarations[0].status === Status.NOT_STARTED_ACTIVITY ? Status.INFORMATIVE : declarations[0].status;

	return {
		month: monthNumber,
		year,
		income: { ...income, total },
		regimes: [...cells.map((cell) => cell.regime), ...(month.payroll ? ['salarios'] : [])],
		status,
		paid: all.filter((declaration) => declaration.status === Status.PRESENTED).every((declaration) => declaration.paid !== false),
		declarations,
	};
};

const annualOf = (annual: EngineAnnualSummary): LegacyAnnual => {
	const presented = annual.state === 'presentada' || annual.state === 'por_pagar';
	const purchased = annual.purchased === true;
	const status = presented
		? Status.PRESENTED
		: purchased
			? Status.IN_PROGRESS
			: annual.state === 'pendiente'
				? Status.PENDING
				: annual.state === 'en_curso'
					? Status.FUTURE
					: Status.NOT_STARTED_ACTIVITY;

	const result = presented && annual.result ? ANNUAL_RESULT[annual.result] : undefined;
	const total = annual.total ?? 0;
	return {
		year: annual.year,
		type: 'annual',
		income: annual.income,
		status,
		isAcquired: purchased,
		hasPresented: presented,
		dueDate: annual.deadline,
		...(annual.daysLate !== undefined ? { daysLate: annual.daysLate } : {}),
		...(presented
			? { paid: annual.state === 'presentada' && (annual.result !== 'a_pagar' || annual.paid === true) }
			: {}),
		...(result ? { result, resultAmount: result === 'IN_FAVOR' ? -total : result === 'TO_PAY' ? total : 0 } : {}),
	};
};

export const adaptNewOverviewToV2 = (overview: EngineOverview): LegacyOverview => {
	const clock = clockOf(overview.today);
	return {
		data: overview.years.map((year) => ({
			year: year.year,
			monthly: year.months.map((month) => monthOf(month, clock)),
			...(year.annual ? { annual: annualOf(year.annual) } : {}),
		})),
	};
};
